import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { findValueInEnum, maybe } from "@saleor/misc";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import useClusterSearch from "@saleor/searches/useClusterSearch";
import useMallQuery from "@saleor/searches/useMallQuery";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  SmopOpStatusEnum,
  SmopRentalTypeEnum,
  SmopStatusEnum,
  SmopUserTypeCode,
  StoreTypeEnum,
  SubTenantTypeEnum
} from "../../types/globalTypes";
import StoreCreatePage, {
  StoreCreatePageFormData
} from "../components/StoreCreatePage/StoreCreatePage";
import { TenantCreateMutation } from "../mutations";
import { usePaymentFeeRate } from "../queries";
import { tenantCreate } from "../types/tenantCreate";
import { storeAddPath, storeListPath } from "../urls";

const StoreCreate: React.SFC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const navigate = useNavigator();
  const { user } = useUser();
  const [isModal, setIsModal] = useState(false);

  const handleStoreAdd = (data: tenantCreate) => {
    if (!maybe(() => data.tenantCreate.errors.length !== 0)) {
      notify({
        status: "success",
        text: intl.formatMessage({ defaultMessage: "Store created" })
      });
      navigate(storeListPath);
    } else if (data.tenantCreate.errors.some(e => e.code === "REQUIRED")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Some Fields have null value!"
        })
      });
    } else if (
      data.tenantCreate.errors.some(e => e.code === "INVALID") &&
      data.tenantCreate.errors.some(e => e.field === "status")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Can't have multiple active tenants from same brand!"
        })
      });
    } else if (
      data.tenantCreate.errors.some(e => e.code === "INVALID") &&
      data.tenantCreate.errors.some(e => e.field === "backgroundImage")
    ) {
      notify({
        status: "error",
        text: data.tenantCreate.errors[0].message
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({ defaultMessage: "Invalid Form!" })
      });
    }
  };

  const { data: paymentFeeRateChoices } = usePaymentFeeRate({
    displayLoader: true
  });

  const brandInitialSearchData = {
    after: null,
    first: 20,
    query: "",
    status: SmopStatusEnum.ACT
  };
  // AUTOCOMPLETE SEARCH
  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: brandsQueryData
  } = useBrandPrivateSearch({
    variables: {
      ...brandInitialSearchData,
      accountManager:
        user.userType === SmopUserTypeCode.ACCOUNT_MANAGER ? user.id : null
    }
  });
  const {
    loadMore: loadMoreMall,
    search: searchMalls,
    result: mallsQueryData
  } = useMallQuery({
    variables: {
      after: null,
      first: 20,
      query: "",
      statuses: [SmopStatusEnum.ACT]
    }
  });
  const {
    loadMore: loadMoreCluster,
    search: searchClusters,
    result: clustersQueryData
  } = useClusterSearch({
    variables: {
      after: null,
      first: 20,
      query: ""
    }
  });

  const sellerBrandChoices = maybe(
    () => brandsQueryData?.data?.search?.edges,
    []
  ).map(edge => edge.node);
  const mallChoices = maybe(() => mallsQueryData?.data?.search?.edges, []).map(
    edge => edge.node
  );
  const clusterChoices = maybe(
    () => clustersQueryData?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  return (
    <TenantCreateMutation onCompleted={handleStoreAdd}>
      {(tenantCreate, tenantCreateResult) => {
        const handleSubmit = async (data: StoreCreatePageFormData, file) => {
          const inputData = {
            bankAccountName: data.acctName,
            bankAccountNo: data.acctNum,
            bankName: data.bankName,
            bannerImage: file,
            brand: data.sellerBrand,
            cluster: data.cluster,
            contractId: data.contractId,
            financeEmail: data.financeEmailAddress,
            financeMobileNo: data.financeMobileNum,
            financeName: data.financeName,
            ipay88SubMid: data.ipay88SubMid,
            isExpressLongDistanceDeliveryAllowed:
              data.isExpressLongDistanceDeliveryAllowed,
            isFeatured: data.isFeatured,
            isOtherBusiness: data.isActivateCompanyDetails,
            isSameDayDeliveryAllowed: data.isSameDayDeliveryAllowed,
            isUseTenantCluster: data.isUseTenantCluster,
            location: data.location,
            mall: data.mallBranch,
            operationEndTime: data.operationEndTime,
            operationStartTime: data.operationStartTime,
            operationStatus: SmopOpStatusEnum.OPN,
            paymentFeeRate: data.paymentFeeRate,
            rentalType:
              maybe(() =>
                findValueInEnum(data.rentalType, SmopRentalTypeEnum)
              ) || null,
            signatoryDesignation: data.designation,
            signatoryEmail: data.authorizeEmailAddress,
            signatoryMobileNo: data.authorizeMobileNum,
            signatoryName: data.signatoryName,
            status: data.isActivate,
            subTenantType: maybe(() => {
              const isFnB = data.storeType === "FOOD_AND_BEVERAGES";
              if (!isFnB) {
                return null;
              }
              if (!data.subTenantType) {
                return null;
              }
              return (
                findValueInEnum(
                  data.subTenantType.replace(/ |-/g, "_").toLocaleUpperCase(),
                  SubTenantTypeEnum
                ) || null
              );
            }),
            tenantType:
              maybe(() => findValueInEnum(data.storeType, StoreTypeEnum)) ||
              null,
            tinNo: data.tinNo
          };
          const result = await tenantCreate({
            variables: {
              input: inputData
            }
          });

          return result.data.tenantCreate.errors;
        };
        return (
          <>
            <StoreCreatePage
              onSubmit={handleSubmit}
              disable={undefined}
              brands={sellerBrandChoices}
              malls={mallChoices}
              saveButtonBarState={tenantCreateResult.status}
              discardButton={setIsModal}
              paymentFeeRateChoices={paymentFeeRateChoices}
              fetchMalls={query => searchMalls(query)}
              fetchBrands={query => searchBrands(query)}
              fetchMoreMalls={{
                hasMore: maybe(
                  () => mallsQueryData.data.search.pageInfo.hasNextPage
                ),
                loading: mallsQueryData.loading,
                onFetchMore: loadMoreMall
              }}
              fetchMoreBrands={{
                hasMore: maybe(
                  () => brandsQueryData.data.search.pageInfo.hasNextPage
                ),
                loading: brandsQueryData.loading,
                onFetchMore: loadMoreBrand
              }}
              fetchMoreClusters={{
                hasMore: maybe(
                  () => clustersQueryData.data.search.pageInfo.hasNextPage
                ),
                loading: clustersQueryData.loading,
                onFetchMore: loadMoreCluster
              }}
              clusters={clusterChoices}
              fetchClusters={query => searchClusters(query)}
            />
            <ActionDialog
              confirmButtonState={"default"}
              onClose={() => {
                navigate(storeAddPath);
                setIsModal(false);
              }}
              onConfirm={() => navigate(storeListPath)}
              open={isModal}
              title={intl.formatMessage({
                defaultMessage: "Discard changes",
                description: "dialog title"
              })}
              variant="default"
            >
              <DialogContentText>
                <FormattedMessage defaultMessage="Changes will not be saved. Do you want to proceed?" />
              </DialogContentText>
            </ActionDialog>
          </>
        );
      }}
    </TenantCreateMutation>
  );
};

export default StoreCreate;
